import { z } from 'zod';

// Adverts, as in Lagos Life: the boards on the map are digital screens. Players book a board for a run of days
// with their own picture and, if they like, a link players go to when they tap it; ads on a board take turns
// (15 seconds each). It is paid with the player's money in the game for now (real payments come later). The
// admins choose whether ads are checked first: every ad, a player's first ad only, or none; an ad that is not
// approved is refunded, one taken down for breaking the rules is not.

// the boards' sizes; each one's size sets its price
export const BOARD_SIZES = {
  small: { label: 'Small board', w: 12, h: 5 },
  medium: { label: 'Board', w: 16, h: 6.5 },
  large: { label: 'Large board', w: 26, h: 10 },
  mega: { label: 'Mega screen', w: 34, h: 13 },
  // at Heathrow, as Lagos Life's airport boards (every flyer passes them)
  airport: { label: 'Airport board', w: 11, h: 4.5 },
} as const;
export type BoardSize = keyof typeof BOARD_SIZES;
export const BOARD_SIZE_KEYS = Object.keys(BOARD_SIZES) as BoardSize[];
// the boards: 1 to 7 in one row along the top of the map, left to right; 201 to 204 at Heathrow Airport
export type Billboard = { number: number; size: BoardSize; where: 'top' | 'airport'; spot?: string };
export const AIRPORT = 'Heathrow Airport';
export const BILLBOARDS: Billboard[] = [
  ...(['medium', 'large', 'small', 'mega', 'small', 'large', 'medium'] as const).map((size, i) => ({
    number: i + 1,
    size,
    where: 'top' as const,
  })),
  ...['Arrivals', 'Departures', 'Car Park', 'Jet Centre'].map((spot, i) => ({
    number: 201 + i,
    size: 'airport' as const,
    where: 'airport' as const,
    spot,
  })),
];
export const boardOf = (number: number) => BILLBOARDS.find((board) => board.number === number) ?? null;

// River plots, as Lagos Life's Sea Plots: a grid of square plots on the Thames at Woolwich Reach. A player buys
// one square of plots (1 × 1, 2 × 2 or 3 × 3) for a run of days and their picture fills it; a plot holds one ad
// at a time, so nothing takes turns there. In the adverts list they are "board" 101, with the plot's top left
// cell (row × columns + column) and its span.
export const RIVER_PLOTS = { board: 101, columns: 25, rows: 7, spans: [1, 2, 3] as const } as const;
export type PlotSpan = (typeof RIVER_PLOTS.spans)[number];
export const isPlots = (board: number) => board === RIVER_PLOTS.board;
const ROW_LETTERS = 'ABCDEFG';
export const plotName = (cell: number, span = 1) => {
  const name = `${ROW_LETTERS[Math.floor(cell / RIVER_PLOTS.columns)] ?? '?'}${(cell % RIVER_PLOTS.columns) + 1}`;
  return span > 1 ? `River plot ${name} · ${span}×${span}` : `River plot ${name}`;
};
// the cells a square of plots covers (empty when it does not fit on the river)
export function plotCells(cell: number, span: number): number[] {
  const { columns, rows } = RIVER_PLOTS;
  const column = cell % columns,
    row = Math.floor(cell / columns);
  if (cell < 0 || column + span > columns || row + span > rows) return [];
  const cells: number[] = [];
  for (let r = 0; r < span; r++) for (let c = 0; c < span; c++) cells.push((row + r) * columns + column + c);
  return cells;
}
export const boardName = (number: number, plot?: { cell: number | null; span: number | null } | null) => {
  if (isPlots(number)) return plot?.cell != null ? plotName(plot.cell, plot.span ?? 1) : 'River plots';
  const board = boardOf(number);
  if (!board) return `Board ${number}`;
  return board.spot
    ? `${BOARD_SIZES[board.size].label} ${number} · ${board.spot}`
    : `${BOARD_SIZES[board.size].label} ${number}`;
};

// the picture players send: resized on their phone to fit 1024 × 512 (the boards are about twice as wide as
// tall; it is fitted, never cropped)
export const ADVERT_IMAGE = { width: 1024, height: 512, maxBytes: 450 * 1024 } as const;
// a river plot's picture is square
export const PLOT_IMAGE = { width: 512, height: 512 } as const;
export const ADVERT_IMAGE_TYPES = {
  jpg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
} as const;
export type AdvertImageType = keyof typeof ADVERT_IMAGE_TYPES;

export const ADVERT_APPROVALS = {
  all: 'Check every ad first',
  first: "Check a player's first ad only",
  none: 'No checks (the team can still take one down)',
} as const;
export type AdvertApproval = keyof typeof ADVERT_APPROVALS;
export const ADVERT_DAY_CHOICES = [1, 3, 7, 14, 28] as const;

const weekPrice = z.number().int().min(100).max(100_000_000);
// a river plot is cheaper (Lagos Life's Sea Plot is about a fourteenth of a billboard)
const plotPrice = z.number().int().min(10).max(10_000_000);
export const AdvertSettings = z.object({
  approval: z.enum(Object.keys(ADVERT_APPROVALS) as [AdvertApproval, ...AdvertApproval[]]),
  // the price of 7 days on each size of board, in pence (a run of other lengths costs the same per day)
  weekPence: z.object({
    small: weekPrice,
    medium: weekPrice,
    large: weekPrice,
    mega: weekPrice,
    airport: weekPrice,
    // one 1 × 1 river plot (a 2 × 2 is four of them, a 3 × 3 nine)
    plot: plotPrice,
  }),
  // the runs players can book
  days: z
    .array(
      z
        .number()
        .int()
        .refine((days) => (ADVERT_DAY_CHOICES as readonly number[]).includes(days), 'Pick from the runs offered'),
    )
    .min(1)
    .max(ADVERT_DAY_CHOICES.length),
  // how long each ad stays up before the next one's turn
  rotateSeconds: z.number().int().min(5).max(60),
  // ads on one board at once (live or waiting to be checked)
  perBoard: z.number().int().min(1).max(50),
  open: z.boolean(),
});
export type AdvertSettings = z.infer<typeof AdvertSettings>;
export const AdvertSettingsReply = z.object({ settings: AdvertSettings });
export type AdvertSettingsReply = z.infer<typeof AdvertSettingsReply>;
export const DEFAULT_ADVERT_SETTINGS: AdvertSettings = {
  approval: 'first',
  weekPence: { small: 15_000, medium: 25_000, large: 40_000, mega: 75_000, airport: 50_000, plot: 1_500 },
  days: [7, 14, 28],
  rotateSeconds: 15,
  perBoard: 12,
  open: true,
};
// what 7 days cost on a board, or on a square of river plots
export const weekPriceOf = (settings: AdvertSettings, board: number, span = 1) =>
  isPlots(board) ? settings.weekPence.plot * span * span : settings.weekPence[boardOf(board)?.size ?? 'medium'];
// what a run costs: the week's price per day, rounded to the penny
export const advertPrice = (settings: AdvertSettings, board: number, days: number, span = 1) =>
  Math.round((weekPriceOf(settings, board, span) * days) / 7);

export const AdvertStatus = z.enum(['pending', 'live', 'ended', 'rejected', 'withdrawn', 'removed']);
export type AdvertStatus = z.infer<typeof AdvertStatus>;
export const ADVERT_STATUS_LABELS: Record<AdvertStatus, string> = {
  pending: 'Waiting to be checked',
  live: 'Live',
  ended: 'Ended',
  rejected: 'Not approved · refunded',
  withdrawn: 'Cancelled · refunded',
  removed: 'Taken down',
};

// an ad on the map: everyone sees these
export const AdvertOnAir = z.object({
  id: z.string().uuid(),
  board: z.number().int(),
  // a river plot's top left cell and span (null on the boards)
  cell: z.number().int().nullable(),
  span: z.number().int().nullable(),
  title: z.string(),
  link: z.string().nullable(),
  // where the picture is (a path on the API)
  image: z.string(),
});
export type AdvertOnAir = z.infer<typeof AdvertOnAir>;
export const AdvertsReply = z.object({
  onAir: z.array(AdvertOnAir),
  settings: AdvertSettings,
  // ads on each board now (live or waiting), so a full board says so before you book
  taken: z.record(z.string(), z.number().int()),
  // the river plots held now (live or waiting to be checked): no one else can buy those cells
  plots: z.array(z.object({ cell: z.number().int(), span: z.number().int() })),
});
export type AdvertsReply = z.infer<typeof AdvertsReply>;

// one of your ads
export const MyAdvert = AdvertOnAir.extend({
  status: AdvertStatus,
  days: z.number().int(),
  pricePence: z.number().int(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
});
export type MyAdvert = z.infer<typeof MyAdvert>;
export const MyAdvertsReply = z.object({ adverts: z.array(MyAdvert) });
export type MyAdvertsReply = z.infer<typeof MyAdvertsReply>;

const httpsLink = z
  .string()
  .trim()
  .max(300)
  .refine((value) => {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' && !url.username && !url.password && url.hostname.includes('.');
    } catch {
      return false;
    }
  }, 'Use a full web address starting with https://');
export const BookAdvertRequest = z.object({
  board: z
    .number()
    .int()
    .refine((board) => isPlots(board) || boardOf(board) !== null, 'That board does not exist'),
  // river plots only: the top left cell and the span
  cell: z
    .number()
    .int()
    .min(0)
    .max(RIVER_PLOTS.columns * RIVER_PLOTS.rows - 1)
    .nullable()
    .default(null),
  span: z
    .number()
    .int()
    .refine((span) => (RIVER_PLOTS.spans as readonly number[]).includes(span), 'Pick a plot size')
    .nullable()
    .default(null),
  days: z.number().int().min(1).max(28),
  title: z.string().trim().min(2, 'Give your ad a name').max(40, 'Keep the name under 40 letters'),
  link: httpsLink.nullable(),
  // the picture as a data address (data:image/jpeg;base64,…)
  image: z.string().max(Math.ceil((ADVERT_IMAGE.maxBytes * 4) / 3) + 40),
});
export type BookAdvertRequest = z.infer<typeof BookAdvertRequest>;
export const BookAdvertReply = z.object({ advert: MyAdvert, walletPence: z.number().int() });
export type BookAdvertReply = z.infer<typeof BookAdvertReply>;

// the admins' list
export const AdminAdvert = MyAdvert.extend({
  owner: z.object({
    userId: z.string().uuid(),
    lifeId: z.string().uuid(),
    username: z.string().nullable(),
    firstName: z.string(),
  }),
  reviewedBy: z.string().nullable(),
  reviewedAt: z.string().nullable(),
});
export type AdminAdvert = z.infer<typeof AdminAdvert>;
export const AdminAdvertsReply = z.object({ adverts: z.array(AdminAdvert), waiting: z.number().int() });
export type AdminAdvertsReply = z.infer<typeof AdminAdvertsReply>;
export const AdvertDecision = z.discriminatedUnion('action', [
  z.object({ action: z.literal('approve') }),
  z.object({ action: z.literal('reject'), reason: z.string().trim().min(3).max(300) }),
  z.object({ action: z.literal('remove'), reason: z.string().trim().min(3).max(300) }),
]);
export type AdvertDecision = z.infer<typeof AdvertDecision>;
